import { get } from 'lodash'
import { ComponentProps, ReactNode } from 'react'
import { useQuery } from 'react-query'

import Image from '@/components/TailwindUI/Image'
import { UserResponse } from '@/components/User'
import cn from '@/utilities/cn'

type UserProfileProps = {
  userId?: string
  enabled?: boolean
  photoUrl?: string
  label?: ReactNode
}
export default function UserProfile({
  userId,
  enabled = false,
  className,
  ...rest
}: UserProfileProps & ComponentProps<'div'>) {
  const userQuery = useQuery<UserResponse, Error>({
    queryKey: [`/v1/admin/organizations/:organizationId/users/${userId}`],
    enabled: enabled && userId != null,
    retry: (failureCount, error: unknown) => {
      if (
        get(error, 'status') === 403 ||
        get(error, 'data') === 'You are not permitted to access this resource' ||
        get(error, 'status') === 404
      ) {
        return false
      } else {
        return true
      }
    }
  })

  const userPhoto = userQuery.data?.data?.photos?.find(photo => photo.primary)?.value
  const givenName = userQuery.data?.data?.name.givenName
  const familyName = userQuery.data?.data?.name.familyName
  const displayName = givenName && familyName ? `${givenName} ${familyName}` : undefined
  return (
    <div className={cn('flex items-center space-x-2 text-left text-sm', className)} {...rest}>
      <Image
        loadingImage={<EmptyProfilePhoto />}
        fallbackImage={<EmptyProfilePhoto />}
        className="fade-in h-6 w-6 rounded-full bg-zinc-100"
        src={userPhoto}
        alt="User Profile Icon"
      />
      {userQuery.isLoading ? (
        <p className="fade-in animate-pulse text-zinc-400 line-clamp-1">Loading...</p>
      ) : (
        <p className="fade-in text-zinc-700 line-clamp-1">{displayName}</p>
      )}
    </div>
  )
}

function EmptyProfilePhoto() {
  return (
    <img
      alt=""
      className="fade-in h-6 w-6 rounded-full bg-zinc-100"
      src="https://cdn.workgrid.com/templates/f8884411-8639-4d72-a6b6-76105f195011/profile.png"
    />
  )
}
